import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { trackEvent } from "../services/analytics";
import {
  Box,
  Card,
  CardContent,
  Grid as Grid2,
  Typography,
  Button,
  Chip,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  CircularProgress,
  Link,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import EmailIcon from "@mui/icons-material/Email";
import PhoneIcon from "@mui/icons-material/Phone";
import LinkedInIcon from "@mui/icons-material/LinkedIn";
import VerifiedIcon from "@mui/icons-material/Verified";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import RefreshIcon from "@mui/icons-material/Refresh";
import HistoryIcon from "@mui/icons-material/History";
import ContactsIcon from "@mui/icons-material/Contacts";
import SearchIcon from "@mui/icons-material/Search";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import TrendingDownIcon from "@mui/icons-material/TrendingDown";
import TrendingFlatIcon from "@mui/icons-material/TrendingFlat";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import NewspaperIcon from "@mui/icons-material/Newspaper";
import MedicationIcon from "@mui/icons-material/Medication";
import PublicIcon from "@mui/icons-material/Public";
import FactoryIcon from "@mui/icons-material/Factory";
import GavelIcon from "@mui/icons-material/Gavel";
import { api, getCandidateDetail, getCandidateInsights, generateCandidateInsights, fetchTrialHistory, getContactsForDrug, pdlFindForDrug, searchDrugPressReleases, addContact, getProbabilityTimeline, ProbabilityTimeline } from "../services/api";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import type { CandidateDetail as CandidateDetailType } from "../types";
import PhaseChip from "../components/PhaseChip";
import ProbabilityBar from "../components/ProbabilityBar";

function formatCurrency(val: number | null | undefined): string {
  if (val === null || val === undefined) return "--";
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 0,
  }).format(val);
}

export default function CandidateDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [data, setData] = useState<CandidateDetailType | null>(null);
  const [loading, setLoading] = useState(true);
  const [insights, setInsights] = useState<Record<string, any> | null>(null);
  const [insightsLoading, setInsightsLoading] = useState(false);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyResult, setHistoryResult] = useState<Record<string, any> | null>(null);
  const [drugContacts, setDrugContacts] = useState<Record<string, any> | null>(null);
  const [pdlSearching, setSnovSearching] = useState(false);
  const [pdlResult, setSnovResult] = useState<Record<string, any> | null>(null);
  const [prSearching, setPrSearching] = useState(false);
  const [prResult, setPrResult] = useState<Record<string, any> | null>(null);
  const [contactForm, setContactForm] = useState({
    first_name: "",
    last_name: "",
    title: "",
    email: "",
    role_category: "",
  });
  const [contactAdding, setContactAdding] = useState(false);
  const [contactAddResult, setContactAddResult] = useState<string | null>(null);
  const [probTimeline, setProbTimeline] = useState<ProbabilityTimeline | null>(null);
  const [selectedTimelinePoint, setSelectedTimelinePoint] = useState<any>(null);
  const [selectedIndication, setSelectedIndication] = useState<string | null>(null);

  useEffect(() => {
    if (!id) return;
    getCandidateDetail(parseInt(id))
      .then((d) => {
        setData(d);
        trackEvent("view_candidate", { drug: d.candidate.drug_name, sponsor: d.candidate.sponsor, phase: d.candidate.phase, id: parseInt(id) });
        // Auto-load stored AI analysis if it exists (never generates new)
        setInsights(null); // Clear any previous drug's analysis
        getCandidateInsights(parseInt(id))
          .then((ins) => {
            if (ins && (ins as any).has_analysis !== false && !ins.error) setInsights(ins);
          })
          .catch(() => {});
        // Load drug-specific contacts
        getContactsForDrug(parseInt(id))
          .then(setDrugContacts)
          .catch(() => {});
        // Load probability timeline
        getProbabilityTimeline(parseInt(id))
          .then(setProbTimeline)
          .catch(() => {});
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [id]);

  const loadInsights = (generate: boolean = false) => {
    if (!id) return;
    setInsightsLoading(true);

    if (!generate) {
      // Just fetch stored analysis
      getCandidateInsights(parseInt(id))
        .then((result) => {
          if (result && (result as any).has_analysis !== false && !result.error) {
            setInsights(result);
          }
        })
        .catch(console.error)
        .finally(() => setInsightsLoading(false));
      return;
    }

    // Generate: POST returns task_id, then poll for completion
    if (data) {
      trackEvent("generate_analysis", { drug: data.candidate.drug_name, sponsor: data.candidate.sponsor, id: parseInt(id) });
    }
    generateCandidateInsights(parseInt(id))
      .then((result) => {
        const taskId = (result as any).task_id;
        if (!taskId) {
          // Legacy: direct response (no task_id)
          if (result && (result as any).has_analysis !== false) {
            setInsights(result);
            getCandidateDetail(parseInt(id)).then(setData);
          }
          setInsightsLoading(false);
          return;
        }
        // Poll for task completion
        const poll = setInterval(async () => {
          try {
            const { data: status } = await api.get(`/sync/status/${taskId}`);
            if (status.status === "complete") {
              clearInterval(poll);
              // Fetch the stored analysis (freshly saved by background task)
              const ins = await getCandidateInsights(parseInt(id));
              if (ins && (ins as any).has_analysis !== false && !ins.error) {
                setInsights(ins);
              }
              // Reload candidate data — AI may have updated launch year, probability, WAC
              getCandidateDetail(parseInt(id)).then(setData);
              setInsightsLoading(false);
            } else if (status.status === "error") {
              clearInterval(poll);
              setInsights({ error: status.error || "Analysis failed" } as any);
              setInsightsLoading(false);
            }
          } catch {
            // Transient error — keep polling
          }
        }, 3000);
      })
      .catch((err) => {
        console.error("Insights generation failed:", err);
        setInsightsLoading(false);
      });
  };

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", mt: 10 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!data) {
    return <Typography>Candidate not found.</Typography>;
  }

  const { candidate: c, wac_projection: wac, trials, events = [] } = data;

  const confidenceColor =
    wac.confidence === "high"
      ? "success"
      : wac.confidence === "medium"
      ? "warning"
      : "error";

  return (
    <Box>
      <Button
        startIcon={<ArrowBackIcon />}
        onClick={() => navigate(-1)}
        sx={{ mb: 2 }}
      >
        Back to Pipeline
      </Button>

      {/* Header */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "flex-start",
            }}
          >
            <Box>
              <Typography variant="h4">{c.drug_name}</Typography>
              {c.generic_name && (
                <Typography variant="subtitle1" color="text.secondary">
                  {c.generic_name}
                </Typography>
              )}
              <Box sx={{ display: "flex", gap: 1, mt: 1 }}>
                <PhaseChip phase={c.phase} />
                <Chip label={c.status} variant="outlined" size="small" />
                {c.therapeutic_area && (
                  <Chip label={c.therapeutic_area} size="small" color="secondary" />
                )}
              </Box>
            </Box>
            <Box sx={{ textAlign: "right" }}>
              {c.nct_id && (
                <Link
                  href={`https://clinicaltrials.gov/study/${c.nct_id}`}
                  target="_blank"
                  rel="noopener"
                  variant="body2"
                >
                  {c.nct_id}
                </Link>
              )}
            </Box>
          </Box>
        </CardContent>
      </Card>

      <Grid2 container spacing={3}>
        {/* Key Metrics */}
        <Grid2 size={{ xs: 12, md: 4 }}>
          <Card sx={{ height: "100%" }}>
            <CardContent>
              <Typography variant="h6" sx={{ mb: 2 }}>
                Key Metrics
              </Typography>
              <Box sx={{ mb: 3 }}>
                <Typography variant="body2" color="text.secondary">
                  Projected Launch Year
                </Typography>
                <Typography variant="h5" fontWeight={700}>
                  {c.projected_launch_year || "--"}
                </Typography>
              </Box>
              <Divider sx={{ my: 2 }} />
              <Typography variant="body2" color="text.secondary">
                Sponsor / Manufacturer
              </Typography>
              <Typography fontWeight={600}>{c.sponsor}</Typography>
              {(c as any).sponsor_subsidiary && (
                <Typography variant="body2" color="text.secondary">
                  via {(c as any).sponsor_subsidiary}
                </Typography>
              )}
              {c.collaborators && (
                <>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mt: 1 }}
                  >
                    Collaborators
                  </Typography>
                  <Typography variant="body2">{c.collaborators}</Typography>
                </>
              )}
              {c.indication && (
                <>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mt: 1 }}
                  >
                    Indication
                  </Typography>
                  <Typography variant="body2">{c.indication}</Typography>
                </>
              )}
              {c.mechanism_of_action && (
                <>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mt: 1 }}
                  >
                    Mechanism of Action
                  </Typography>
                  <Typography variant="body2">
                    {c.mechanism_of_action}
                  </Typography>
                </>
              )}
            </CardContent>
          </Card>
        </Grid2>

        {/* WAC Projection */}
        <Grid2 size={{ xs: 12, md: 4 }}>
          <Card sx={{ height: "100%" }}>
            <CardContent>
              <Typography variant="h6" sx={{ mb: 2 }}>
                Projected WAC
              </Typography>
              <Box sx={{ textAlign: "center", mb: 2 }}>
                <Typography variant="h4" fontWeight={700} color="primary">
                  {formatCurrency(wac.wac_median)}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Median Annual Estimate
                </Typography>
              </Box>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  mb: 2,
                }}
              >
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    Low
                  </Typography>
                  <Typography fontWeight={600}>
                    {formatCurrency(wac.wac_low)}
                  </Typography>
                </Box>
                <Box sx={{ textAlign: "right" }}>
                  <Typography variant="body2" color="text.secondary">
                    High
                  </Typography>
                  <Typography fontWeight={600}>
                    {formatCurrency(wac.wac_high)}
                  </Typography>
                </Box>
              </Box>
              <Divider sx={{ my: 2 }} />
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
                <Typography variant="body2" color="text.secondary">
                  Confidence:
                </Typography>
                <Chip
                  label={wac.confidence.toUpperCase()}
                  size="small"
                  color={confidenceColor as any}
                />
              </Box>
              <Typography variant="body2" color="text.secondary">
                Based on {wac.comparable_count} comparable product
                {wac.comparable_count !== 1 ? "s" : ""}
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: "block" }}>
                {wac.basis}
              </Typography>
            </CardContent>
          </Card>
        </Grid2>

        {/* Dosing & Administration */}
        <Grid2 size={{ xs: 12, md: 4 }}>
          <Card sx={{ height: "100%" }}>
            <CardContent>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
                <MedicationIcon sx={{ color: "primary.main" }} />
                <Typography variant="h6">Dosing & Administration</Typography>
              </Box>
              {c.dosing_frequency && (
                <Box sx={{ mb: 1.5 }}>
                  <Typography variant="body2" color="text.secondary">
                    Dosing Frequency
                  </Typography>
                  <Chip
                    label={c.dosing_frequency.charAt(0).toUpperCase() + c.dosing_frequency.slice(1)}
                    size="small"
                    color="primary"
                    variant="outlined"
                  />
                </Box>
              )}
              {c.dose_amount && (
                <Box sx={{ mb: 1.5 }}>
                  <Typography variant="body2" color="text.secondary">
                    Dose
                  </Typography>
                  <Typography fontWeight={600}>{c.dose_amount}</Typography>
                </Box>
              )}
              {c.injection_volume && (
                <Box sx={{ mb: 1.5 }}>
                  <Typography variant="body2" color="text.secondary">
                    Injection Volume
                  </Typography>
                  <Typography fontWeight={600}>{c.injection_volume}</Typography>
                </Box>
              )}
              {c.self_administered !== null && c.self_administered !== undefined && (
                <Box sx={{ mb: 1.5 }}>
                  <Typography variant="body2" color="text.secondary">
                    Self-Administered
                  </Typography>
                  <Chip
                    label={c.self_administered ? "Yes (Patient)" : "No (HCP)"}
                    size="small"
                    color={c.self_administered ? "success" : "default"}
                  />
                </Box>
              )}
              {c.administration_setting && (
                <Box sx={{ mb: 1.5 }}>
                  <Typography variant="body2" color="text.secondary">
                    Setting
                  </Typography>
                  <Typography fontWeight={600}>
                    {c.administration_setting.charAt(0).toUpperCase() + c.administration_setting.slice(1)}
                  </Typography>
                </Box>
              )}
              {c.route_detail && (
                <Box sx={{ mb: 1.5 }}>
                  <Typography variant="body2" color="text.secondary">
                    Route
                  </Typography>
                  <Typography fontWeight={600}>{c.route_detail}</Typography>
                </Box>
              )}
              {c.delivery_device && (
                <Box sx={{ mb: 1.5 }}>
                  <Typography variant="body2" color="text.secondary">
                    Delivery Device
                  </Typography>
                  <Typography fontWeight={600}>{c.delivery_device}</Typography>
                </Box>
              )}
              {!c.dosing_frequency && !c.dose_amount && !c.self_administered && !c.route_detail && (
                <Typography variant="body2" color="text.secondary">
                  No dosing data available yet. Run Device Enrichment or Dosing Enrichment sync to populate.
                </Typography>
              )}
            </CardContent>
          </Card>
        </Grid2>

        {/* Regulatory, Manufacturing, Geographic — show if any data exists */}
        {(c.pdufa_date || c.adcom_date || c.manufacturing_partner || c.device_partner || c.geographic_launch_sequence) && (
          <Grid2 size={12}>
            <Card>
              <CardContent>
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 3 }}>
                  {/* PDUFA / AdCom */}
                  {(c.pdufa_date || c.adcom_date) && (
                    <Box sx={{ minWidth: 200 }}>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mb: 1 }}>
                        <GavelIcon sx={{ fontSize: 18, color: "#c62828" }} />
                        <Typography variant="subtitle2" fontWeight={700}>Regulatory Milestones</Typography>
                      </Box>
                      {c.pdufa_date && (
                        <Typography variant="body2">
                          <strong>PDUFA Date:</strong> {c.pdufa_date}
                        </Typography>
                      )}
                      {c.adcom_date && (
                        <Typography variant="body2">
                          <strong>Advisory Committee:</strong> {c.adcom_date}
                        </Typography>
                      )}
                    </Box>
                  )}

                  {/* Manufacturing Partners */}
                  {(c.manufacturing_partner || c.device_partner) && (
                    <Box sx={{ minWidth: 200 }}>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mb: 1 }}>
                        <FactoryIcon sx={{ fontSize: 18, color: "#4e342e" }} />
                        <Typography variant="subtitle2" fontWeight={700}>Partners</Typography>
                      </Box>
                      {c.manufacturing_partner && (
                        <Typography variant="body2">
                          <strong>CDMO:</strong> {c.manufacturing_partner}
                        </Typography>
                      )}
                      {c.device_partner && (
                        <Typography variant="body2">
                          <strong>Device:</strong> {c.device_partner}
                        </Typography>
                      )}
                    </Box>
                  )}

                  {/* Geographic Launch */}
                  {c.geographic_launch_sequence && (
                    <Box sx={{ minWidth: 200 }}>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mb: 1 }}>
                        <PublicIcon sx={{ fontSize: 18, color: "#1565c0" }} />
                        <Typography variant="subtitle2" fontWeight={700}>Geographic Launch</Typography>
                      </Box>
                      <Typography variant="body2">{c.geographic_launch_sequence}</Typography>
                    </Box>
                  )}
                </Box>
              </CardContent>
            </Card>
          </Grid2>
        )}

        {/* Probability of Approval — full width, above contacts */}
        <Grid2 size={12}>
          <Card>
            <CardContent>
              <Grid2 container spacing={3}>
                <Grid2 size={{ xs: 12, md: 4 }}>
                  <Typography variant="h6" sx={{ mb: 2 }}>Chance of Approval</Typography>
                  <ProbabilityBar value={c.probability_of_success} />
                  {data.markov && (
                    <Box sx={{ mt: 1.5 }}>
                      {c.projected_launch_year && (
                        <Typography variant="body2" sx={{ mb: 1 }}>
                          Est. launch: <strong>{c.projected_launch_year}</strong>
                        </Typography>
                      )}
                      {/* Plain language signals from the Markov model */}
                      {data.markov.positive_signals?.length > 0 && (
                        <Typography variant="caption" display="block" sx={{ color: "text.secondary", lineHeight: 1.8 }}>
                          <Typography component="span" variant="caption" sx={{ color: "success.main", fontWeight: 600 }}>Scoring higher</Typography>
                          {": "}{data.markov.positive_signals.join(", ")}
                        </Typography>
                      )}
                      {data.markov.negative_signals?.length > 0 && (
                        <Typography variant="caption" display="block" sx={{ color: "text.secondary", lineHeight: 1.8 }}>
                          <Typography component="span" variant="caption" sx={{ color: "error.main", fontWeight: 600 }}>Scoring lower</Typography>
                          {": "}{data.markov.negative_signals.join(", ")}
                        </Typography>
                      )}
                    </Box>
                  )}
                  {data.indications && data.indications.length > 0 && (
                    <Box sx={{ mt: 2 }}>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                        Indications ({data.indications.length})
                      </Typography>
                      {data.indications.map((ind, i) => {
                        const isSelected = selectedIndication
                          ? ind.indication === selectedIndication
                          : ind.is_lead;
                        return (
                        <Box
                          key={i}
                          onClick={() => {
                            setSelectedIndication(ind.indication);
                            if (id) {
                              getProbabilityTimeline(parseInt(id), ind.indication)
                                .then(setProbTimeline)
                                .catch(() => {});
                            }
                          }}
                          sx={{
                            display: "flex", alignItems: "center", gap: 1, mb: 0.5,
                            cursor: "pointer", borderRadius: 1, px: 1, py: 0.5,
                            bgcolor: isSelected ? "action.selected" : "transparent",
                            "&:hover": { bgcolor: "action.hover" },
                          }}
                        >
                          <Box sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: isSelected ? "primary.main" : "grey.400", flexShrink: 0 }} />
                          <Typography variant="body2" sx={{ flex: 1, fontWeight: isSelected ? 700 : 400 }}>{ind.indication}</Typography>
                          <Chip label={ind.phase} size="small" sx={{ height: 20, fontSize: "0.7rem" }} />
                          {ind.probability != null && (
                            <Typography variant="body2" fontWeight={600} sx={{ minWidth: 35, textAlign: "right" }}>{Math.round(ind.probability * 100)}%</Typography>
                          )}
                          <Typography variant="caption" color="text.secondary">{ind.trial_count} trial{ind.trial_count !== 1 ? "s" : ""}</Typography>
                        </Box>
                        );
                      })}
                    </Box>
                  )}
                </Grid2>
                <Grid2 size={{ xs: 12, md: 8 }}>
                  {probTimeline && probTimeline.timeline.length > 1 && (() => {
                    const chartData = probTimeline.timeline.filter((p) => p.probability != null).map((p, idx) => ({
                      date: p.date.slice(0, 7), probability: Math.round((p.probability || 0) * 100),
                      event: p.event, phase: p.phase, trial_title: p.trial_title,
                      nct_id: p.nct_id, enrollment: p.enrollment, indication: p.indication, idx,
                    }));
                    return (
                    <Box>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>Approval Trend</Typography>
                      <Box sx={{ width: "100%", height: 220, cursor: "pointer" }}>
                        <ResponsiveContainer>
                          <LineChart data={chartData} margin={{ top: 5, right: 10, left: -15, bottom: 5 }}
                            onClick={(e: any) => { if (e?.activePayload?.[0]) setSelectedTimelinePoint(e.activePayload[0].payload); }}>
                            <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
                            <XAxis dataKey="date" tick={{ fontSize: 10 }} />
                            <YAxis domain={[0, 100]} tick={{ fontSize: 10 }} tickFormatter={(v: number) => `${v}%`} />
                            <Tooltip content={({ active, payload }: any) => {
                              if (!active || !payload?.[0]) return null;
                              const d = payload[0].payload;
                              return (<Box sx={{ bgcolor: "background.paper", border: 1, borderColor: "divider", p: 1, borderRadius: 1, maxWidth: 250 }}>
                                <Typography variant="caption" fontWeight={700}>{d.date} — {d.probability}%</Typography>
                                <Typography variant="caption" display="block" color="text.secondary">{d.event}</Typography>
                                {d.trial_title && <Typography variant="caption" display="block" sx={{ mt: 0.5 }}>{d.trial_title}</Typography>}
                              </Box>);
                            }} />
                            <Line type="stepAfter" dataKey="probability" stroke="#1565c0" strokeWidth={2}
                              dot={{ r: 3, fill: "#1565c0" }} activeDot={{ r: 6, stroke: "#1565c0", strokeWidth: 2, fill: "#fff" }} />
                          </LineChart>
                        </ResponsiveContainer>
                      </Box>
                      {selectedTimelinePoint && (
                        <Box sx={{ mt: 1, p: 1.5, bgcolor: "grey.50", borderRadius: 1, border: 1, borderColor: "divider" }}>
                          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                            <Typography variant="body2" fontWeight={700}>{selectedTimelinePoint.event}</Typography>
                            <Typography variant="body2" fontWeight={700} color="primary">{selectedTimelinePoint.probability}%</Typography>
                          </Box>
                          <Typography variant="caption" color="text.secondary">{selectedTimelinePoint.date} — {selectedTimelinePoint.phase}</Typography>
                          {selectedTimelinePoint.trial_title && <Typography variant="caption" display="block" sx={{ mt: 0.5 }}>{selectedTimelinePoint.trial_title}</Typography>}
                          {selectedTimelinePoint.nct_id && (
                            <Link href={`https://clinicaltrials.gov/study/${selectedTimelinePoint.nct_id}`} target="_blank" rel="noopener" sx={{ fontSize: "0.75rem" }}>{selectedTimelinePoint.nct_id}</Link>
                          )}
                        </Box>
                      )}
                    </Box>);
                  })()}
                </Grid2>
              </Grid2>
            </CardContent>
          </Card>
        </Grid2>

        {/* Contacts — relevant to this drug, grouped by role */}
        <Grid2 size={12}>
          <Card>
            <CardContent>
              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
                <Box>
                  <Typography variant="h6">
                    Relevant Contacts at {c.sponsor}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Filtered for {c.therapeutic_area || "this area"} · {c.route_detail || "Injectable"} · {c.phase}
                  </Typography>
                </Box>
                <Button
                  size="small"
                  startIcon={<ContactsIcon />}
                  onClick={() => navigate(`/contacts?drug=${encodeURIComponent(c.drug_name)}`)}
                >
                  All Contacts
                </Button>
              </Box>

              {!drugContacts || (drugContacts as any).total_contacts === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  No contacts on file for {c.sponsor}. Run Contact Discovery sync or use Email Enrichment to find contacts.
                </Typography>
              ) : (() => {
                const groups = (drugContacts as any).groups || [];
                const roleColors: Record<string, string> = {
                  "Commercial Operations": "#1565c0",
                  "Market Access": "#6a1b9a",
                  "Payer Strategy": "#4527a0",
                  "Launch Team": "#e65100",
                  "Device/Delivery": "#00838f",
                  "Specialty Pharmacy": "#2e7d32",
                  "Trade & Distribution": "#1a237e",
                  "Medical Affairs": "#c62828",
                  "HEOR": "#ad1457",
                  "Pharmacovigilance": "#f57f17",
                  "Digital/Data": "#00897b",
                  "CMC/Manufacturing": "#4e342e",
                  "Business Development": "#0d47a1",
                  "Regulatory": "#37474f",
                  "Clinical Operations": "#bf360c",
                  "Quality": "#827717",
                };

                if (groups.length === 0 || groups.every((g: any) => g.contacts.length === 0)) {
                  return (
                    <Box>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        {(drugContacts as any).total_contacts} contacts at {c.sponsor}, but none matched relevant roles for this drug.
                      </Typography>
                      <Button
                        variant="contained"
                        startIcon={pdlSearching ? <CircularProgress size={16} color="inherit" /> : <SearchIcon />}
                        onClick={() => {
                          if (!id) return;
                          setSnovSearching(true);
                          trackEvent("pdl_search", { drug: c.drug_name, sponsor: c.sponsor });
                          pdlFindForDrug(parseInt(id))
                            .then((res) => {
                              setSnovResult(res);
                              getContactsForDrug(parseInt(id)).then(setDrugContacts);
                            })
                            .catch(console.error)
                            .finally(() => setSnovSearching(false));
                        }}
                        disabled={pdlSearching}
                        sx={{ mb: 2 }}
                      >
                        {pdlSearching ? "Searching..." : `Find Relevant People at ${c.sponsor}`}
                      </Button>
                      <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5, mb: 1 }}>
                        Searches People Data Labs for market access, commercial, device, launch, medical affairs, and other relevant roles at {c.sponsor}. Uses ~15-30 PDL credits.
                      </Typography>
                      {pdlResult && (
                        <Typography variant="body2" color="success.main" display="block" sx={{ mb: 1 }}>
                          Found {(pdlResult as any).total_found} relevant contacts across {(pdlResult as any).roles_searched} roles, added {(pdlResult as any).new_contacts_added} new. Credits used: {(pdlResult as any).credits_used}
                        </Typography>
                      )}
                    </Box>
                  );
                }

                return (
                  <>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      {(drugContacts as any).relevant_contacts} relevant of {(drugContacts as any).total_contacts} total contacts at {c.sponsor}
                    </Typography>
                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2 }}>
                      {groups.map((group: any) => (
                        <Box
                          key={group.role}
                          sx={{
                            flex: "1 1 280px",
                            maxWidth: "380px",
                            border: "1px solid #e0e0e0",
                            borderRadius: 2,
                            p: 1.5,
                          }}
                        >
                          <Chip
                            label={`${group.role} (${group.contacts.length})`}
                            size="small"
                            sx={{
                              mb: 0.5,
                              backgroundColor: (roleColors[group.role] || "#666") + "18",
                              color: roleColors[group.role] || "#666",
                              fontWeight: 700,
                              fontSize: 12,
                            }}
                          />
                          <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                            {group.relevance}
                          </Typography>
                          {group.contacts.length === 0 ? (
                            <Typography variant="caption" color="text.secondary">
                              No contacts yet — add via Companies page
                            </Typography>
                          ) : (
                            group.contacts.map((contact: any, idx: number) => (
                              <Box key={idx} sx={{ py: 0.5, borderBottom: idx < group.contacts.length - 1 ? "1px solid #f5f5f5" : "none" }}>
                                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                                  <Typography fontWeight={600} variant="body2" sx={{ fontSize: 13 }}>
                                    {contact.name}
                                  </Typography>
                                  {contact.verified && (
                                    <VerifiedIcon sx={{ fontSize: 12, color: "success.main" }} />
                                  )}
                                </Box>
                                <Typography variant="caption" color="text.secondary" display="block">
                                  {contact.title || ""}
                                </Typography>
                                <Box sx={{ display: "flex", gap: 0.5, mt: 0.3 }}>
                                  {contact.email && (
                                    <Link href={`mailto:${contact.email}`}>
                                      <EmailIcon sx={{ fontSize: 14 }} />
                                    </Link>
                                  )}
                                  {contact.phone && (
                                    <Link href={`tel:${contact.phone}`}>
                                      <PhoneIcon sx={{ fontSize: 14 }} />
                                    </Link>
                                  )}
                                  {contact.linkedin && (
                                    <Link href={contact.linkedin} target="_blank" rel="noopener">
                                      <LinkedInIcon sx={{ fontSize: 14 }} />
                                    </Link>
                                  )}
                                </Box>
                              </Box>
                            ))
                          )}
                        </Box>
                      ))}
                    </Box>
                  </>
                );
              })()}

              {/* Add Contact Form */}
              {(drugContacts as any)?.company_id && (
                <Box sx={{ mt: 3, pt: 2, borderTop: "1px solid #e0e0e0" }}>
                  <Typography variant="subtitle2" sx={{ mb: 1 }}>
                    Add Contact
                  </Typography>
                  <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", alignItems: "flex-end" }}>
                    <TextField
                      size="small"
                      label="First Name"
                      value={contactForm.first_name}
                      onChange={(e) => setContactForm({ ...contactForm, first_name: e.target.value })}
                      sx={{ width: 130 }}
                    />
                    <TextField
                      size="small"
                      label="Last Name"
                      value={contactForm.last_name}
                      onChange={(e) => setContactForm({ ...contactForm, last_name: e.target.value })}
                      sx={{ width: 130 }}
                    />
                    <TextField
                      size="small"
                      label="Title"
                      value={contactForm.title}
                      onChange={(e) => setContactForm({ ...contactForm, title: e.target.value })}
                      sx={{ width: 180 }}
                    />
                    <TextField
                      size="small"
                      label="Email"
                      value={contactForm.email}
                      onChange={(e) => setContactForm({ ...contactForm, email: e.target.value })}
                      sx={{ width: 200 }}
                    />
                    <FormControl size="small" sx={{ minWidth: 160 }}>
                      <InputLabel>Role Category</InputLabel>
                      <Select
                        value={contactForm.role_category}
                        label="Role Category"
                        onChange={(e) => setContactForm({ ...contactForm, role_category: e.target.value })}
                      >
                        <MenuItem value="">None</MenuItem>
                        <MenuItem value="Commercial Operations">Commercial Operations</MenuItem>
                        <MenuItem value="Market Access">Market Access</MenuItem>
                        <MenuItem value="Payer Strategy">Payer Strategy</MenuItem>
                        <MenuItem value="Launch Team">Launch Team</MenuItem>
                        <MenuItem value="Device/Delivery">Device/Delivery</MenuItem>
                        <MenuItem value="Specialty Pharmacy">Specialty Pharmacy</MenuItem>
                        <MenuItem value="Trade & Distribution">Trade & Distribution</MenuItem>
                        <MenuItem value="Medical Affairs">Medical Affairs</MenuItem>
                        <MenuItem value="HEOR">HEOR</MenuItem>
                        <MenuItem value="Pharmacovigilance">Pharmacovigilance</MenuItem>
                        <MenuItem value="Digital/Data">Digital/Data</MenuItem>
                        <MenuItem value="CMC/Manufacturing">CMC/Manufacturing</MenuItem>
                        <MenuItem value="Business Development">Business Development</MenuItem>
                        <MenuItem value="Regulatory">Regulatory</MenuItem>
                        <MenuItem value="Clinical Operations">Clinical Operations</MenuItem>
                        <MenuItem value="Quality">Quality</MenuItem>
                      </Select>
                    </FormControl>
                    <Button
                      variant="contained"
                      size="small"
                      disabled={contactAdding || !contactForm.first_name || !contactForm.last_name}
                      onClick={async () => {
                        const companyId = (drugContacts as any)?.company_id;
                        if (!companyId) return;
                        setContactAdding(true);
                        setContactAddResult(null);
                        try {
                          const res = await addContact(companyId, {
                            first_name: contactForm.first_name,
                            last_name: contactForm.last_name,
                            title: contactForm.title || undefined,
                            email: contactForm.email || undefined,
                            role_category: contactForm.role_category || undefined,
                          });
                          setContactAddResult(res.message);
                          setContactForm({ first_name: "", last_name: "", title: "", email: "", role_category: "" });
                          // Reload contacts
                          if (id) getContactsForDrug(parseInt(id)).then(setDrugContacts);
                        } catch (err) {
                          setContactAddResult("Error adding contact");
                        } finally {
                          setContactAdding(false);
                        }
                      }}
                    >
                      {contactAdding ? "Adding..." : "Add"}
                    </Button>
                  </Box>
                  {contactAddResult && (
                    <Typography variant="caption" color="success.main" sx={{ mt: 0.5, display: "block" }}>
                      {contactAddResult}
                    </Typography>
                  )}
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid2>

        {/* Events & Press Releases */}
        <Grid2 size={12}>
          <Card>
            <CardContent>
              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <NewspaperIcon sx={{ color: "primary.main" }} />
                  <Typography variant="h6">Events & Press Releases</Typography>
                  <Chip label={events.length} size="small" sx={{ ml: 1 }} />
                </Box>
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={prSearching ? <CircularProgress size={14} /> : <SearchIcon />}
                  onClick={() => {
                    if (!id) return;
                    setPrSearching(true);
                    trackEvent("press_release_search", { drug: c.drug_name });
                    searchDrugPressReleases(parseInt(id))
                      .then((res) => {
                        setPrResult(res);
                        // Reload candidate data to get new events
                        getCandidateDetail(parseInt(id)).then(setData);
                      })
                      .catch(console.error)
                      .finally(() => setPrSearching(false));
                  }}
                  disabled={prSearching}
                >
                  {prSearching ? "Searching web..." : "Search Web for News"}
                </Button>
              </Box>

              {prResult && (
                <Typography variant="caption" color="success.main" display="block" sx={{ mb: 1 }}>
                  Found {(prResult as any).articles_found} articles, {(prResult as any).new_events_created} new events added
                </Typography>
              )}

              {events.length === 0 && !prResult ? (
                <Typography color="text.secondary">
                  No events tracked yet. Click "Search Web for News" to find articles about {c.drug_name}.
                </Typography>
              ) : (
                <Box>
                  {events.map((evt: any, idx: number) => {
                    const eventTypeColors: Record<string, string> = {
                      trial_result: "#1565c0",
                      regulatory_filing: "#6a1b9a",
                      fda_action: "#2e7d32",
                      partnership: "#e65100",
                      manufacturing: "#00838f",
                      launch_signal: "#c62828",
                      regulatory_designation: "#4527a0",
                      press_release: "#616161",
                    };
                    const eventTypeLabels: Record<string, string> = {
                      trial_result: "Trial Result",
                      regulatory_filing: "Regulatory Filing",
                      fda_action: "FDA Action",
                      partnership: "Partnership",
                      manufacturing: "Manufacturing",
                      launch_signal: "Launch Signal",
                      regulatory_designation: "Designation",
                      press_release: "Press Release",
                    };
                    const color = eventTypeColors[evt.event_type] || "#616161";
                    const label = eventTypeLabels[evt.event_type] || evt.event_type;

                    return (
                      <Box
                        key={evt.id || idx}
                        sx={{
                          py: 1.5,
                          borderBottom: idx < events.length - 1 ? "1px solid #e0e0e0" : "none",
                          display: "flex",
                          gap: 2,
                          alignItems: "flex-start",
                        }}
                      >
                        {/* Date column */}
                        <Box sx={{ minWidth: 80, flexShrink: 0 }}>
                          <Typography variant="body2" fontWeight={600} color="text.secondary">
                            {evt.event_date
                              ? new Date(evt.event_date + "T00:00:00").toLocaleDateString("en-US", {
                                  month: "short",
                                  day: "numeric",
                                  year: "numeric",
                                })
                              : "--"}
                          </Typography>
                        </Box>

                        {/* Type chip */}
                        <Box sx={{ minWidth: 120, flexShrink: 0 }}>
                          <Chip
                            label={label}
                            size="small"
                            sx={{
                              backgroundColor: color + "18",
                              color: color,
                              fontWeight: 700,
                              fontSize: 11,
                            }}
                          />
                        </Box>

                        {/* Content */}
                        <Box sx={{ flex: 1 }}>
                          <Typography variant="body2" fontWeight={600}>
                            {evt.title}
                          </Typography>
                          {evt.summary && (
                            <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.3 }}>
                              {evt.summary.length > 200 ? evt.summary.slice(0, 200) + "..." : evt.summary}
                            </Typography>
                          )}
                          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 0.5 }}>
                            <Typography variant="caption" color="text.secondary">
                              {evt.source_name}
                            </Typography>
                            {evt.source_url && (
                              <Link
                                href={evt.source_url}
                                target="_blank"
                                rel="noopener"
                                sx={{ display: "flex", alignItems: "center" }}
                              >
                                <OpenInNewIcon sx={{ fontSize: 12 }} />
                              </Link>
                            )}
                          </Box>
                        </Box>

                        {/* Timeline impact indicator */}
                        <Box sx={{ minWidth: 36, textAlign: "center", flexShrink: 0 }}>
                          {evt.timeline_impact === "accelerates" && (
                            <TrendingUpIcon sx={{ color: "#2e7d32", fontSize: 20 }} titleAccess={`Accelerates${evt.timeline_impact_months ? ` ~${evt.timeline_impact_months}mo` : ""}`} />
                          )}
                          {evt.timeline_impact === "delays" && (
                            <TrendingDownIcon sx={{ color: "#c62828", fontSize: 20 }} titleAccess={`Delays${evt.timeline_impact_months ? ` ~${evt.timeline_impact_months}mo` : ""}`} />
                          )}
                          {evt.timeline_impact === "neutral" && (
                            <TrendingFlatIcon sx={{ color: "#9e9e9e", fontSize: 20 }} titleAccess="Neutral" />
                          )}
                        </Box>
                      </Box>
                    );
                  })}
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid2>

        {/* Clinical Trials */}
        <Grid2 size={12}>
          <Card>
            <CardContent>
              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
                <Typography variant="h6">
                  Clinical Trials
                </Typography>
                <Button
                  size="small"
                  startIcon={historyLoading ? <CircularProgress size={14} /> : <HistoryIcon />}
                  onClick={() => {
                    if (!id) return;
                    setHistoryLoading(true);
                    trackEvent("fetch_trial_history", { drug: c.drug_name });
                    fetchTrialHistory(parseInt(id))
                      .then((result) => {
                        setHistoryResult(result);
                        // Reload candidate data to get updated trials
                        getCandidateDetail(parseInt(id)).then(setData);
                      })
                      .catch(console.error)
                      .finally(() => setHistoryLoading(false));
                  }}
                  disabled={historyLoading}
                >
                  {historyLoading ? "Fetching..." : "Fetch Full History"}
                </Button>
              </Box>
              {historyResult && (
                <Typography variant="caption" color="success.main" sx={{ mb: 1, display: "block" }}>
                  Found {(historyResult as any).total_trials} total trials, added {(historyResult as any).new_trials_added} new
                </Typography>
              )}
              {trials.length === 0 ? (
                <Typography color="text.secondary">
                  No trial records found.
                </Typography>
              ) : (() => {
                // Group trials by indication
                const grouped: Record<string, typeof trials> = {};
                for (const t of trials) {
                  const key = t.conditions || "Unknown Indication";
                  if (!grouped[key]) grouped[key] = [];
                  grouped[key].push(t);
                }
                const groups = Object.entries(grouped).sort((a, b) => b[1].length - a[1].length);
                const hasMultipleGroups = groups.length > 1;

                return groups.map(([indication, groupTrials]) => (
                  <Box key={indication} sx={{ mb: hasMultipleGroups ? 3 : 0 }}>
                    {hasMultipleGroups && (
                      <Typography variant="subtitle2" sx={{ mb: 1, mt: 1, color: "primary.main" }}>
                        {indication} ({groupTrials.length} trial{groupTrials.length !== 1 ? "s" : ""})
                      </Typography>
                    )}
                    <Table size="small">
                      {(!hasMultipleGroups || groups.indexOf([indication, groupTrials]) === 0 || true) && (
                        <TableHead>
                          <TableRow>
                            <TableCell>NCT ID</TableCell>
                            <TableCell>Title</TableCell>
                            <TableCell>Phase</TableCell>
                            <TableCell>Status</TableCell>
                            <TableCell>Enrollment</TableCell>
                          </TableRow>
                        </TableHead>
                      )}
                      <TableBody>
                        {groupTrials.map((t) => (
                          <TableRow key={t.nct_id}>
                            <TableCell>
                              <Link
                                href={`https://clinicaltrials.gov/study/${t.nct_id}`}
                                target="_blank"
                                rel="noopener"
                              >
                                {t.nct_id}
                              </Link>
                            </TableCell>
                            <TableCell sx={{ maxWidth: 350, whiteSpace: "normal", wordBreak: "break-word" }}>
                              <Typography variant="body2">{t.title}</Typography>
                            </TableCell>
                            <TableCell>
                              <PhaseChip phase={t.phase} />
                            </TableCell>
                            <TableCell>{t.status}</TableCell>
                            <TableCell>
                              {t.enrollment?.toLocaleString() || "--"}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </Box>
                ));
              })()}
            </CardContent>
          </Card>
        </Grid2>

        {/* AI Insights */}
        <Grid2 size={12}>
          <Card>
            <CardContent>
              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <AutoAwesomeIcon sx={{ color: "primary.main" }} />
                  <Typography variant="h6">AI Analysis</Typography>
                </Box>
                <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
                  {insights && (insights as any)._generated_at && (
                    <Typography variant="caption" color="text.secondary" sx={{ mr: 1 }}>
                      Generated: {new Date((insights as any)._generated_at).toLocaleDateString("en-US", {
                        month: "short", day: "numeric", year: "numeric"
                      })}
                    </Typography>
                  )}
                  {insights && (
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={insightsLoading ? <CircularProgress size={14} /> : <RefreshIcon />}
                      onClick={() => loadInsights(true)}
                      disabled={insightsLoading}
                    >
                      {insightsLoading ? "Generating..." : "Refresh Analysis"}
                    </Button>
                  )}
                  {!insights && (
                    <Button
                      variant="contained"
                      startIcon={insightsLoading ? <CircularProgress size={16} color="inherit" /> : <AutoAwesomeIcon />}
                      onClick={() => loadInsights(true)}
                      disabled={insightsLoading}
                    >
                      {insightsLoading ? "Generating..." : "Generate Analysis"}
                    </Button>
                  )}
                </Box>
              </Box>

              {insightsLoading && !insights && (
                <Box sx={{ textAlign: "center", py: 4 }}>
                  <CircularProgress />
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                    Analyzing {c.drug_name} — checking trial data, competitive landscape, patent status, and device strategy...
                  </Typography>
                </Box>
              )}

              {insights && !insights.error && (
                <Box>
                  {/* 1. Commercial Opportunity */}
                  {(insights.commercial_opportunity || insights.bd_opportunity_summary) && (() => {
                    const opp = (insights.commercial_opportunity || insights.bd_opportunity_summary) as any;
                    return (
                    <Box sx={{ mb: 3 }}>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
                        <Typography variant="subtitle1" fontWeight={700}>
                          Commercial Opportunity
                        </Typography>
                        <Chip
                          label={opp.opportunity_rating}
                          size="small"
                          color={
                            opp.opportunity_rating === "high" ? "success" :
                            opp.opportunity_rating === "medium" ? "warning" : "default"
                          }
                        />
                      </Box>
                      <Typography variant="body2">
                        {opp.rationale}
                      </Typography>
                      {opp.key_engagement_timing && (
                        <Typography variant="body2" sx={{ mt: 1 }}>
                          <strong>Timing:</strong> {opp.key_engagement_timing || opp.timing}
                        </Typography>
                      )}
                      {(opp.partnership_opportunities || opp.recommended_call_points || []).length > 0 && (
                        <>
                          <Typography variant="body2" fontWeight={600} sx={{ mt: 1 }}>
                            Opportunities:
                          </Typography>
                          {(opp.partnership_opportunities || opp.recommended_call_points || []).map((p: string, i: number) => (
                            <Typography key={i} variant="body2" sx={{ pl: 2 }}>- {p}</Typography>
                          ))}
                        </>
                      )}
                    </Box>
                    );
                  })()}

                  <Divider sx={{ my: 2 }} />

                  {/* 2. Delivery & Formulation */}
                  {(insights.delivery_and_formulation || insights.device_and_delivery_insights) && (() => {
                    const dev = (insights.delivery_and_formulation || insights.device_and_delivery_insights) as any;
                    return (
                    <Box sx={{ mb: 3 }}>
                      <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1 }}>
                        Delivery & Formulation
                      </Typography>
                      <Typography variant="body2">
                        {dev.formulation_strategy || dev.device_strategy_assessment}
                      </Typography>
                      {dev.patient_administration && (
                        <Typography variant="body2" sx={{ mt: 1 }}>
                          <strong>Administration:</strong> {dev.patient_administration}
                        </Typography>
                      )}
                    </Box>
                    );
                  })()}

                  <Divider sx={{ my: 2 }} />

                  {/* 2b. Dosing & Administration (AI) */}
                  {insights.dosing_and_administration && (
                    <Box sx={{ mb: 3 }}>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
                        <MedicationIcon sx={{ fontSize: 18, color: "primary.main" }} />
                        <Typography variant="subtitle1" fontWeight={700}>
                          Dosing & Administration
                        </Typography>
                      </Box>
                      {(insights.dosing_and_administration as any).dosing_regimen && (
                        <Typography variant="body2">
                          <strong>Regimen:</strong> {(insights.dosing_and_administration as any).dosing_regimen}
                        </Typography>
                      )}
                      <Box sx={{ display: "flex", gap: 1, mt: 1, flexWrap: "wrap" }}>
                        {(insights.dosing_and_administration as any).self_administered !== undefined && (
                          <Chip
                            label={(insights.dosing_and_administration as any).self_administered ? "Self-Administered" : "HCP-Administered"}
                            size="small"
                            color={(insights.dosing_and_administration as any).self_administered ? "success" : "default"}
                          />
                        )}
                        {(insights.dosing_and_administration as any).administration_setting && (
                          <Chip
                            label={(insights.dosing_and_administration as any).administration_setting}
                            size="small"
                            variant="outlined"
                          />
                        )}
                      </Box>
                      {(insights.dosing_and_administration as any).dosing_convenience_vs_competitors && (
                        <Typography variant="body2" sx={{ mt: 1 }}>
                          <strong>vs. Competitors:</strong> {(insights.dosing_and_administration as any).dosing_convenience_vs_competitors}
                        </Typography>
                      )}
                    </Box>
                  )}

                  <Divider sx={{ my: 2 }} />

                  {/* 2c. Manufacturing & Partnerships (AI) */}
                  {insights.manufacturing_and_partnerships && (
                    <Box sx={{ mb: 3 }}>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
                        <FactoryIcon sx={{ fontSize: 18, color: "#4e342e" }} />
                        <Typography variant="subtitle1" fontWeight={700}>
                          Manufacturing & Partnerships
                        </Typography>
                      </Box>
                      {(insights.manufacturing_and_partnerships as any).known_cdmo && (
                        <Typography variant="body2">
                          <strong>CDMO:</strong> {(insights.manufacturing_and_partnerships as any).known_cdmo}
                        </Typography>
                      )}
                      {(insights.manufacturing_and_partnerships as any).known_device_partner && (
                        <Typography variant="body2" sx={{ mt: 0.5 }}>
                          <strong>Device Partner:</strong> {(insights.manufacturing_and_partnerships as any).known_device_partner}
                        </Typography>
                      )}
                      {(insights.manufacturing_and_partnerships as any).manufacturing_readiness && (
                        <Typography variant="body2" sx={{ mt: 0.5 }}>
                          <strong>Readiness:</strong> {(insights.manufacturing_and_partnerships as any).manufacturing_readiness}
                        </Typography>
                      )}
                    </Box>
                  )}

                  <Divider sx={{ my: 2 }} />

                  {/* 3. Launch Timeline */}
                  {insights.launch_timeline_analysis && (
                    <Box sx={{ mb: 3 }}>
                      <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1 }}>
                        Launch Timeline
                      </Typography>
                      <Typography variant="body2">
                        <strong>Projected:</strong> {(insights.launch_timeline_analysis as any).projected_launch}
                        {" "}({(insights.launch_timeline_analysis as any).confidence} confidence)
                      </Typography>
                      {((insights.launch_timeline_analysis as any).key_milestones || []).map((m: string, i: number) => (
                        <Typography key={i} variant="body2" sx={{ pl: 2 }}>- {m}</Typography>
                      ))}
                    </Box>
                  )}

                  <Divider sx={{ my: 2 }} />

                  {/* 3b. Geographic Launch Sequence (AI) */}
                  {insights.geographic_launch && (
                    <Box sx={{ mb: 3 }}>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
                        <PublicIcon sx={{ fontSize: 18, color: "#1565c0" }} />
                        <Typography variant="subtitle1" fontWeight={700}>
                          Geographic Launch Sequence
                        </Typography>
                      </Box>
                      <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", mb: 1 }}>
                        {(insights.geographic_launch as any).us_launch && (
                          <Box sx={{ minWidth: 100 }}>
                            <Typography variant="caption" color="text.secondary">US</Typography>
                            <Typography variant="body2" fontWeight={600}>
                              {(insights.geographic_launch as any).us_launch}
                            </Typography>
                          </Box>
                        )}
                        {(insights.geographic_launch as any).eu_launch && (
                          <Box sx={{ minWidth: 100 }}>
                            <Typography variant="caption" color="text.secondary">EU</Typography>
                            <Typography variant="body2" fontWeight={600}>
                              {(insights.geographic_launch as any).eu_launch}
                            </Typography>
                          </Box>
                        )}
                        {(insights.geographic_launch as any).japan_launch && (
                          <Box sx={{ minWidth: 100 }}>
                            <Typography variant="caption" color="text.secondary">Japan</Typography>
                            <Typography variant="body2" fontWeight={600}>
                              {(insights.geographic_launch as any).japan_launch}
                            </Typography>
                          </Box>
                        )}
                        {(insights.geographic_launch as any).other_markets && (
                          <Box sx={{ minWidth: 100 }}>
                            <Typography variant="caption" color="text.secondary">Other</Typography>
                            <Typography variant="body2" fontWeight={600}>
                              {(insights.geographic_launch as any).other_markets}
                            </Typography>
                          </Box>
                        )}
                      </Box>
                      {(insights.geographic_launch as any).launch_sequence_rationale && (
                        <Typography variant="body2">
                          <strong>Rationale:</strong> {(insights.geographic_launch as any).launch_sequence_rationale}
                        </Typography>
                      )}
                    </Box>
                  )}

                  <Divider sx={{ my: 2 }} />

                  {/* 4. Competitive Landscape */}
                  {insights.competitive_landscape && (
                    <Box sx={{ mb: 3 }}>
                      <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1 }}>
                        Competitive Landscape
                      </Typography>
                      <Chip
                        label={(insights.competitive_landscape as any).market_position}
                        size="small"
                        variant="outlined"
                        sx={{ mb: 1 }}
                      />
                      <Typography variant="body2">
                        <strong>Differentiation:</strong> {(insights.competitive_landscape as any).differentiation}
                      </Typography>
                      {((insights.competitive_landscape as any).key_competitors || []).map((comp: string, i: number) => (
                        <Typography key={i} variant="body2" sx={{ pl: 2 }}>- {comp}</Typography>
                      ))}
                      {(insights.competitive_landscape as any).head_to_head_data && (
                        <Typography variant="body2" sx={{ mt: 1 }}>
                          <strong>Head-to-Head Data:</strong> {(insights.competitive_landscape as any).head_to_head_data}
                        </Typography>
                      )}
                      {(insights.competitive_landscape as any).vs_standard_of_care && (
                        <Typography variant="body2" sx={{ mt: 0.5 }}>
                          <strong>vs. Standard of Care:</strong> {(insights.competitive_landscape as any).vs_standard_of_care}
                        </Typography>
                      )}
                    </Box>
                  )}

                  <Divider sx={{ my: 2 }} />

                  {/* 5. Clinical Outlook */}
                  {insights.clinical_outlook && (
                    <Box sx={{ mb: 3 }}>
                      <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1 }}>
                        Clinical Outlook
                      </Typography>
                      <Typography variant="body2">
                        <strong>Efficacy:</strong> {(insights.clinical_outlook as any).efficacy_assessment}
                      </Typography>
                      <Typography variant="body2" sx={{ mt: 0.5 }}>
                        <strong>Unmet need:</strong> {(insights.clinical_outlook as any).unmet_need}
                      </Typography>
                      <Typography variant="body2" sx={{ mt: 0.5 }}>
                        <strong>Patient population:</strong> {(insights.clinical_outlook as any).patient_population_size}
                      </Typography>
                      <Typography variant="body2" sx={{ mt: 0.5 }}>
                        <strong>Standard of care:</strong> {(insights.clinical_outlook as any).standard_of_care}
                      </Typography>
                      {(insights.clinical_outlook as any).clinical_differentiation && (
                        <Typography variant="body2" sx={{ mt: 0.5 }}>
                          <strong>Differentiation:</strong> {(insights.clinical_outlook as any).clinical_differentiation}
                        </Typography>
                      )}
                      {(insights.clinical_outlook as any).vs_standard_of_care && (
                        <Typography variant="body2" sx={{ mt: 0.5 }}>
                          <strong>vs. Standard of Care:</strong> {(insights.clinical_outlook as any).vs_standard_of_care}
                        </Typography>
                      )}
                      {((insights.clinical_outlook as any).clinical_risks || []).length > 0 && (
                        <Box sx={{ mt: 1 }}>
                          <Typography variant="body2" fontWeight={600} color="error.main">
                            Clinical Risks:
                          </Typography>
                          {((insights.clinical_outlook as any).clinical_risks || []).map((r: string, i: number) => (
                            <Typography key={i} variant="body2" sx={{ pl: 2 }}>- {r}</Typography>
                          ))}
                        </Box>
                      )}
                    </Box>
                  )}

                  <Divider sx={{ my: 2 }} />

                  {/* 6. Approval Probability */}
                  {insights.approval_probability_analysis && (
                    <Box sx={{ mb: 3 }}>
                      <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1 }}>
                        Approval Probability
                      </Typography>
                      <Box sx={{ display: "flex", gap: 2, mb: 1 }}>
                        <Chip label={`Model: ${Math.round((c.probability_of_success || 0) * 100)}%`} size="small" />
                        <Chip
                          label={`AI Adjusted: ${(insights.approval_probability_analysis as any).adjusted_probability}%`}
                          size="small"
                          color="primary"
                        />
                      </Box>
                      <Typography variant="body2" fontWeight={600} color="success.main" sx={{ mt: 1 }}>
                        Positive Drivers:
                      </Typography>
                      {((insights.approval_probability_analysis as any).drivers_positive || []).map((d: string, i: number) => (
                        <Typography key={i} variant="body2" sx={{ pl: 2 }}>+ {d}</Typography>
                      ))}
                      <Typography variant="body2" fontWeight={600} color="error.main" sx={{ mt: 1 }}>
                        Risk Factors:
                      </Typography>
                      {((insights.approval_probability_analysis as any).drivers_negative || []).map((d: string, i: number) => (
                        <Typography key={i} variant="body2" sx={{ pl: 2 }}>- {d}</Typography>
                      ))}
                      {(insights.approval_probability_analysis as any).regulatory_signals && (
                        <Typography variant="body2" sx={{ mt: 1 }}>
                          <strong>Regulatory:</strong> {(insights.approval_probability_analysis as any).regulatory_signals}
                        </Typography>
                      )}
                      {(insights.approval_probability_analysis as any).adjustment_rationale && (
                        <Typography variant="body2" sx={{ mt: 1 }}>
                          <strong>Adjustment Rationale:</strong> {(insights.approval_probability_analysis as any).adjustment_rationale}
                        </Typography>
                      )}
                    </Box>
                  )}

                  <Divider sx={{ my: 2 }} />

                  {/* 6b. Regulatory Milestones (AI) */}
                  {insights.regulatory_milestones && (
                    <Box sx={{ mb: 3 }}>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
                        <GavelIcon sx={{ fontSize: 18, color: "#c62828" }} />
                        <Typography variant="subtitle1" fontWeight={700}>
                          Regulatory Milestones
                        </Typography>
                      </Box>
                      {(insights.regulatory_milestones as any).pdufa_date && (
                        <Typography variant="body2" sx={{ mb: 0.5 }}>
                          <strong>PDUFA Date:</strong> {(insights.regulatory_milestones as any).pdufa_date}
                        </Typography>
                      )}
                      {(insights.regulatory_milestones as any).adcom_scheduled && (
                        <Typography variant="body2" sx={{ mb: 0.5 }}>
                          <strong>Advisory Committee:</strong> {(insights.regulatory_milestones as any).adcom_scheduled}
                        </Typography>
                      )}
                      <Box sx={{ display: "flex", gap: 0.5, mt: 1, flexWrap: "wrap" }}>
                        {(insights.regulatory_milestones as any).breakthrough_designation && (
                          <Chip label="Breakthrough" size="small" color="success" />
                        )}
                        {(insights.regulatory_milestones as any).fast_track && (
                          <Chip label="Fast Track" size="small" color="info" />
                        )}
                        {(insights.regulatory_milestones as any).priority_review && (
                          <Chip label="Priority Review" size="small" color="warning" />
                        )}
                        {(insights.regulatory_milestones as any).orphan_drug && (
                          <Chip label="Orphan Drug" size="small" color="secondary" />
                        )}
                        {(insights.regulatory_milestones as any).accelerated_approval && (
                          <Chip label="Accelerated Approval" size="small" color="error" />
                        )}
                      </Box>
                    </Box>
                  )}

                  <Divider sx={{ my: 2 }} />

                  {/* 7. Patent & Biosimilar */}
                  {insights.patent_and_biosimilar_analysis && (
                    <Box sx={{ mb: 3 }}>
                      <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1 }}>
                        Patent & Biosimilar Threat
                      </Typography>
                      <Typography variant="body2">
                        <strong>Patent:</strong> {(insights.patent_and_biosimilar_analysis as any).patent_situation}
                      </Typography>
                      <Typography variant="body2">
                        <strong>Estimated Expiry:</strong> {(insights.patent_and_biosimilar_analysis as any).estimated_patent_expiry}
                      </Typography>
                      <Box sx={{ display: "flex", gap: 1, mt: 1 }}>
                        <Typography variant="body2"><strong>Biosimilar Threat:</strong></Typography>
                        <Chip
                          label={(insights.patent_and_biosimilar_analysis as any).biosimilar_threat}
                          size="small"
                          color={
                            (insights.patent_and_biosimilar_analysis as any).biosimilar_threat === "high" ? "error" :
                            (insights.patent_and_biosimilar_analysis as any).biosimilar_threat === "medium" ? "warning" : "success"
                          }
                        />
                      </Box>
                      <Typography variant="body2" sx={{ mt: 0.5 }}>
                        {(insights.patent_and_biosimilar_analysis as any).biosimilar_impact}
                      </Typography>
                    </Box>
                  )}

                  <Divider sx={{ my: 2 }} />

                  {/* Pricing Estimate */}
                  {insights.pricing_estimate && (
                    <Box sx={{ mb: 3 }}>
                      <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1 }}>
                        Pricing Estimate
                      </Typography>
                      {(insights.pricing_estimate as any).wac_estimate && (
                        <Typography variant="body2">
                          <strong>WAC Estimate:</strong> {(insights.pricing_estimate as any).wac_estimate}
                        </Typography>
                      )}
                      {(insights.pricing_estimate as any).rationale && (
                        <Typography variant="body2" sx={{ mt: 0.5 }}>
                          <strong>Rationale:</strong> {(insights.pricing_estimate as any).rationale}
                        </Typography>
                      )}
                      {((insights.pricing_estimate as any).comparable_drug_prices || []).length > 0 && (
                        <Box sx={{ mt: 1 }}>
                          <Typography variant="body2" fontWeight={600}>
                            Comparable Drug Prices:
                          </Typography>
                          {((insights.pricing_estimate as any).comparable_drug_prices || []).map((cp: any, i: number) => (
                            <Typography key={i} variant="body2" sx={{ pl: 2 }}>
                              - {typeof cp === "string" ? cp : `${cp.drug_name || cp.name}: ${cp.price || cp.wac || ""}`}
                            </Typography>
                          ))}
                        </Box>
                      )}
                    </Box>
                  )}

                  <Divider sx={{ my: 2 }} />

                  {/* 8. Mechanism of Action */}
                  {insights.mechanism_of_action && (
                    <Box sx={{ mb: 3 }}>
                      <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1 }}>
                        Mechanism of Action
                      </Typography>
                      <Typography variant="body2">
                        {(insights.mechanism_of_action as any).mechanism_summary}
                      </Typography>
                      <Box sx={{ display: "flex", gap: 1, mt: 1, flexWrap: "wrap" }}>
                        {(insights.mechanism_of_action as any).target && (
                          <Chip label={`Target: ${(insights.mechanism_of_action as any).target}`} size="small" variant="outlined" />
                        )}
                        {(insights.mechanism_of_action as any).drug_class && (
                          <Chip label={(insights.mechanism_of_action as any).drug_class} size="small" variant="outlined" />
                        )}
                        {(insights.mechanism_of_action as any).mechanism_validated && (
                          <Chip
                            label={`Validated: ${(insights.mechanism_of_action as any).mechanism_validated}`}
                            size="small"
                            color={(insights.mechanism_of_action as any).mechanism_validated === "yes" ? "success" : "warning"}
                          />
                        )}
                      </Box>
                      {(insights.mechanism_of_action as any).novel_aspects && (
                        <Typography variant="body2" sx={{ mt: 1 }}>
                          <strong>Novel aspects:</strong> {(insights.mechanism_of_action as any).novel_aspects}
                        </Typography>
                      )}
                    </Box>
                  )}

                  <Divider sx={{ my: 2 }} />

                  {/* 9. Safety Profile */}
                  {insights.safety_profile && (
                    <Box sx={{ mb: 3 }}>
                      <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1 }}>
                        Safety Profile
                      </Typography>
                      {((insights.safety_profile as any).known_safety_signals || []).map((s: string, i: number) => (
                        <Typography key={i} variant="body2" sx={{ pl: 2 }}>- {s}</Typography>
                      ))}
                      <Typography variant="body2" sx={{ mt: 1 }}>
                        <strong>Class history:</strong> {(insights.safety_profile as any).class_safety_history}
                      </Typography>
                      <Box sx={{ display: "flex", gap: 1, mt: 1, flexWrap: "wrap" }}>
                        <Chip
                          label={`Black Box Risk: ${(insights.safety_profile as any).black_box_risk}`}
                          size="small"
                          color={(insights.safety_profile as any).black_box_risk === "high" ? "error" : (insights.safety_profile as any).black_box_risk === "medium" ? "warning" : "success"}
                        />
                        <Chip
                          label={`Immunogenicity: ${(insights.safety_profile as any).immunogenicity_risk}`}
                          size="small"
                          color={(insights.safety_profile as any).immunogenicity_risk === "high" ? "error" : (insights.safety_profile as any).immunogenicity_risk === "medium" ? "warning" : "success"}
                        />
                      </Box>
                      {(insights.safety_profile as any).injection_site_reactions && (
                        <Typography variant="body2" sx={{ mt: 1 }}>
                          <strong>Injection site reactions:</strong> {(insights.safety_profile as any).injection_site_reactions}
                        </Typography>
                      )}
                    </Box>
                  )}

                  <Divider sx={{ my: 2 }} />
                  <Typography variant="caption" color="text.secondary">
                    Analysis by Claude AI
                    {(insights as any)._generated_at && ` | Generated: ${new Date((insights as any)._generated_at).toLocaleDateString()}`}
                    {(insights as any)._tokens_used && ` | Credits: ${(insights as any)._tokens_used}`}
                    {(insights as any)._from_cache && " | Loaded from saved analysis"}
                    {" | "}Based on available pipeline data — verify key claims independently.
                  </Typography>
                </Box>
              )}

              {insights?.error && (
                <Typography color="error">{String(insights.error)}</Typography>
              )}
            </CardContent>
          </Card>
        </Grid2>
      </Grid2>
    </Box>
  );
}
